import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LADEZUSTAND (Auftrag 17.09.2026, Abschnitt 19)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Keine unschönen springenden Layouts. Skeletons, dezente Spinner,
 *    Progress bei längeren Berechnungen, klare Statusmeldungen.
 *    Wenn echter Fortschritt nicht verfügbar ist, KEINEN fingierten
 *    Prozentwert anzeigen."
 *
 * Deshalb ist `fortschritt` eine Zahl oder gar nichts. Es gibt keine
 * Betriebsart „Balken läuft von allein" - ein Balken, der sich bewegt,
 * ohne dass etwas gemessen wurde, ist eine Behauptung über den Serverlauf.
 *
 * Das Skelett zeichnet so viele Zeilen, wie danach wirklich kommen
 * (`zeilen`), damit der Inhalt nicht springt. Es hat KEINE Wanderanimation:
 * In einer Engineering-Anwendung, in der eine Rohrnetzrechnung Minuten
 * läuft, ist ein flackernder Bildschirm eine Zumutung.
 */

export interface LoadingStateProps {
  /** Was gerade geschieht. Vorgabe „wird geladen …". */
  text?: string;
  /** Darstellung: Zeilenskelett (Vorgabe) oder nur ein dezenter Kreisel. */
  art?: 'skelett' | 'spinner';
  /** Anzahl der Skelettzeilen - so viele, wie danach zu erwarten sind. */
  zeilen?: number;
  /** Spaltenzahl des Skeletts; ergibt ein Gitter statt bloßer Balken. */
  spalten?: number;
  /**
   * Fortschritt in Prozent, NUR wenn er tatsächlich gemessen wurde
   * (z. B. „412 von 1.200 Räumen"). `undefined` heißt: es gibt keinen -
   * dann erscheint auch kein Balken.
   */
  fortschritt?: number;
  /** Ergänzt den Balken um die gezählten Größen, z. B. „412 von 1.200". */
  fortschrittText?: string;
  className?: string;
}

export function LoadingState({
  text = 'wird geladen …',
  art = 'skelett',
  zeilen = 5,
  spalten = 1,
  fortschritt,
  fortschrittText,
  className,
}: LoadingStateProps) {
  const zeigeFortschritt = typeof fortschritt === 'number'
    && Number.isFinite(fortschritt);
  const wert = zeigeFortschritt
    ? Math.max(0, Math.min(100, fortschritt as number))
    : 0;

  return (
    <div
      className={`m-laden${className ? ` ${className}` : ''}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="m-laden-kopf">
        <span className="m-laden-kreisel" aria-hidden="true" />
        <span className="m-laden-text">{text}</span>
      </div>

      {zeigeFortschritt && (
        <div className="m-laden-fortschritt">
          <div
            className="m-laden-balken"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(wert)}
            aria-label={text}
          >
            <span style={{ width: `${wert}%` }} />
          </div>
          <span className="m-laden-fortschritt-text">
            {fortschrittText ?? `${Math.round(wert)} %`}
          </span>
        </div>
      )}

      {art === 'skelett' && (
        <div className="m-laden-skelett" aria-hidden="true">
          {Array.from({ length: Math.max(1, zeilen) }, (_, z) => (
            <div className="m-laden-skelett-zeile" key={z}>
              {Array.from({ length: Math.max(1, spalten) }, (_, s) => (
                <span className="m-laden-skelett-feld" key={s} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
